import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Megaphone,
  Inbox,
  Flame,
  Coins,
  Clock,
  Plus,
  TrendingUp,
  AlertTriangle,
  Mail,
  Sparkles,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import {
  subscribeCampaigns,
  subscribeLeads,
  subscribeSyncJobs,
  categoryStyle,
  categoryLabel,
} from "../../services/collavueStore";
import PageHeader from "../../components/collavue/PageHeader";
import Empty from "../../components/collavue/Empty";
import { decodeHtmlEntities } from "../../utils/htmlEntities";
import { storageGet, storageSet } from "../../utils/browserSafety";

const DAILY_SYNC_BANNER_KEY = "collavue.dashboard.dailySyncBannerDismissed";

export default function DashboardPage() {
  const { user, userProfile } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);
  const [leads, setLeads] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [dailyBannerDismissed, setDailyBannerDismissed] = useState(
    () => storageGet(DAILY_SYNC_BANNER_KEY) === "1"
  );

  useEffect(() => {
    if (!user) return;
    const a = subscribeCampaigns((items) => {
      setCampaigns(items);
      setCampaignsLoaded(true);
    });
    const b = subscribeLeads(null, setLeads);
    const c = subscribeSyncJobs(setJobs);
    return () => {
      a();
      b();
      c();
    };
  }, [user]);

  const dismissDailyBanner = () => {
    setDailyBannerDismissed(true);
    storageSet(DAILY_SYNC_BANNER_KEY, "1");
  };

  // Surface the daily email sync to users who have campaigns but none
  // enrolled — it's the highest-value feature they're not using. Dismissible
  // so it never becomes wallpaper.
  const showDailySyncBanner =
    campaignsLoaded &&
    campaigns.length > 0 &&
    !campaigns.some((c) => c.autoSyncDaily) &&
    !dailyBannerDismissed;

  const credits = Number(userProfile?.credits ?? 0);
  const lowCredits = credits < 10;

  const today = new Date().toDateString();
  const newToday = leads.filter((l) => {
    const ts = l.createdAt?.toMillis ? l.createdAt.toMillis() : l.createdAt;
    return ts && new Date(ts).toDateString() === today;
  }).length;
  const hot = leads.filter((l) => l.category === "hot").length;
  const warm = leads.filter((l) => l.category === "warm").length;
  const lastSync = jobs[0]?.startedAt?.toDate?.()
    ? jobs[0].startedAt.toDate().toLocaleString()
    : "Never";

  const recent = useMemo(() => leads.slice(0, 8), [leads]);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 bg-[#fafafa] min-h-screen font-sans text-[#0B1038]">
      <PageHeader
        title={
          userProfile?.displayName
            ? `Welcome back, ${userProfile.displayName.split(" ")[0]}.`
            : "Welcome back."
        }
        subtitle="Your lead pipeline at a glance — synced and scored, ready to action."
        actions={
          <Link
            to="/campaigns/new"
            data-tutorial="new-campaign-cta"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#0B1038] text-white text-sm font-semibold hover:bg-[#151c54] transition-colors"
          >
            <Plus className="w-4 h-4" />
            New campaign
          </Link>
        }
      />

      {lowCredits && (
        <div className="mb-5 flex items-start gap-3 p-3 border border-amber-200 bg-amber-50 rounded-sm text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div>
            <strong className="block">Credits running low</strong>
            You have <strong>{credits}</strong> credits left.{" "}
            <Link to="/billing" className="underline font-semibold">
              Top up
            </Link>{" "}
            or upgrade to continue syncing campaigns.
          </div>
        </div>
      )}

      {campaignsLoaded && campaigns.length === 0 && (
        <div className="mb-6 bg-white border border-gray-200 rounded-md p-5 sm:p-6">
          <h2 className="font-display text-2xl text-[#0B1038] tracking-tight">
            Get your first leads in 3 steps
          </h2>
          <p className="text-sm text-gray-600 mt-1.5">
            Collavue finds people on X (Twitter), LinkedIn, and Reddit already talking
            about the problem you solve — then delivers new ones to your email
            every day.
          </p>
          <div className="mt-5 grid sm:grid-cols-3 gap-3">
            <GuideStep
              n={1}
              title="Create a campaign"
              body="Paste your website (or pick a template) and the AI builds your search strategy. Takes ~2 minutes."
            />
            <GuideStep
              n={2}
              title="Run your first sync"
              body="We search X, LinkedIn, and Reddit, score every post, and fill your inbox with leads worth replying to."
            />
            <GuideStep
              n={3}
              title="Turn on the daily email"
              body="Flip on daily auto-sync and wake up to fresh leads in your email — plus your saved AI searches, run automatically."
            />
          </div>
          <Link
            to="/campaigns/new"
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-[#0B1038] text-white text-sm font-semibold hover:bg-[#151c54] transition-colors"
          >
            <Plus className="w-4 h-4" /> Create your first campaign
          </Link>
        </div>
      )}

      {showDailySyncBanner && (
        <div className="mb-5 flex items-start gap-3 p-4 border border-blue-200 bg-blue-50 rounded-sm text-sm text-blue-900">
          <Mail className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <strong className="block">
              Get new leads in your email every morning
            </strong>
            Turn on daily auto-sync and Collavue searches X, LinkedIn, and Reddit
            overnight, then emails you a digest — new leads, hot-lead count,
            and your saved{" "}
            <Link to="/leads" className="underline font-semibold">
              lead-assistant
            </Link>{" "}
            command results. Enable it from any campaign's sync settings.
            <div className="mt-2">
              <Link
                to="/campaigns"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5" /> Set up daily sync
              </Link>
            </div>
          </div>
          <button
            onClick={dismissDailyBanner}
            aria-label="Dismiss daily sync banner"
            className="p-1 text-blue-400 hover:text-blue-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Stat Icon={Megaphone} label="Active campaigns" value={campaigns.length} />
        <Stat
          Icon={Inbox}
          label="New leads today"
          value={newToday}
          accent="text-blue-600"
        />
        <Stat Icon={Flame} label="Hot leads" value={hot} accent="text-rose-600" />
        <Stat Icon={Coins} label="Credits" value={credits} accent="text-blue-600" />
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl text-[#0B1038] tracking-tight">
              Recent leads
            </h2>
            <Link
              to="/leads"
              className="text-sm text-blue-600 hover:underline font-medium"
            >
              View all →
            </Link>
          </div>
          {recent.length === 0 ? (
            <Empty
              icon={Inbox}
              title="No leads yet"
              body="Create a campaign and run a sync to find buying-intent posts."
              cta={
                <Link
                  to="/campaigns/new"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#0B1038] text-white font-semibold hover:bg-[#151c54]"
                >
                  <Plus className="w-4 h-4" /> Create campaign
                </Link>
              }
            />
          ) : (
            <ul className="divide-y divide-gray-100">
              {recent.map((l) => (
                <li key={l.id} className="py-3">
                  <Link
                    to={`/leads/${l.id}`}
                    className="flex items-start justify-between gap-3 group"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-[#0B1038] truncate group-hover:text-blue-600 transition-colors">
                        {decodeHtmlEntities(l.title) || decodeHtmlEntities(l.body)?.slice(0, 90)}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {l.platform} · {l.community || "—"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold text-[#0B1038]">
                        {l.intentScore}
                      </div>
                      <span
                        className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-sm border mt-1 ${categoryStyle(
                          l.category
                        )}`}
                      >
                        {categoryLabel(l.category)}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-5">
          <h2 className="font-display text-xl text-[#0B1038] tracking-tight mb-4">
            Performance summary
          </h2>
          <Metric label="Hot leads" value={hot} icon={Flame} />
          <Metric label="Warm leads" value={warm} icon={TrendingUp} />
          <Metric label="Last sync" value={lastSync} icon={Clock} />
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
            <Link
              to="/campaigns"
              className="block w-full text-center px-3 py-2 rounded-sm border border-gray-300 text-sm font-semibold hover:bg-gray-50"
            >
              Manage campaigns
            </Link>
            <Link
              to="/brand-voice"
              className="block w-full text-center px-3 py-2 rounded-sm border border-gray-300 text-sm font-semibold hover:bg-gray-50"
            >
              Train brand voice
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ Icon, label, value, accent = "text-[#0B1038]" }) {
  return (
    <div className="bg-white border border-gray-200 rounded-md p-4">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
          {label}
        </span>
        <Icon className="w-4 h-4 text-gray-400" />
      </div>
      <div className={`font-display text-3xl mt-1 tabular-nums ${accent}`}>
        {value}
      </div>
    </div>
  );
}

function GuideStep({ n, title, body }) {
  return (
    <div className="p-4 rounded-sm border border-gray-200 bg-gray-50">
      <div className="w-7 h-7 rounded-full bg-[#0B1038] text-white text-xs font-bold inline-flex items-center justify-center">
        {n}
      </div>
      <div className="mt-2 text-sm font-semibold text-[#0B1038]">{title}</div>
      <p className="mt-1 text-xs text-gray-600 leading-relaxed">{body}</p>
    </div>
  );
}

function Metric({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-gray-600 inline-flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" />} {label}
      </span>
      <span className="font-semibold text-[#0B1038]">{value}</span>
    </div>
  );
}
